import React, { useState } from 'react';
import { Settings as SettingsIcon, Users, Trash2, AlertTriangle } from 'lucide-react';
import { User } from '../types';
import { USERS } from '../data/authData';

interface SettingsProps { user: User; onClearData: () => void; }

const ROLE_LABELS: Record<string, string> = { admin: 'Admin', coo: 'COO', md: 'MD', cto: 'CTO', employee: 'Employee' };
const ROLE_BADGE: Record<string, string> = { admin: 'badge-red', coo: 'badge-gold', md: 'badge-gold', cto: 'badge-teal', employee: 'badge-amber' };

export default function Settings({ user, onClearData }: SettingsProps) {
  const [confirmClear, setConfirmClear] = useState(false);

  return (
    <div style={{ padding: 28, maxWidth: 800 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28 }}>
        <SettingsIcon size={22} color="var(--gold)" />
        <h1>Settings</h1>
      </div>

      {/* Users */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Users size={16} />
            <h3>Users</h3>
          </div>
        </div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>NPNL</th><th>See Cost</th></tr></thead>
            <tbody>
              {USERS.map(u => (
                <tr key={u.id}>
                  <td style={{ fontWeight: 500 }}>{u.name}</td>
                  <td style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{u.email}</td>
                  <td><span className={`badge ${ROLE_BADGE[u.role]}`}>{ROLE_LABELS[u.role]}</span></td>
                  <td>{u.canSeeNPNL ? '✓' : '—'}</td>
                  <td>{u.canSeeInternalCost ? '✓' : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 12 }}>
          To add/remove users or change passwords, update authData.ts and rebuild the app.
        </p>
      </div>

      {/* App info */}
      <div className="card" style={{ marginBottom: 20 }}>
        <h3 style={{ marginBottom: 14 }}>App Information</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {[
            ['Version', '2.0.0'],
            ['Build', 'Shivam Studio Rate Planner'],
            ['Storage', 'localStorage (Phase 1)'],
            ['Phase 2', 'Hostinger MySQL (planned)'],
            ['Vendors', '4 (Purab Digital, Max Album, Vandana Album, Arang Video Films)'],
            ['Contractors', '6 active (Shubh Gunn, Uttam Rai, Gunabh, Faizul, Guru, Jitendra)'],
          ].map(([k, v]) => (
            <div key={k} style={{ padding: '8px 12px', background: 'var(--bg-hover)', borderRadius: 6 }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>{k}</div>
              <div style={{ fontSize: 13 }}>{v}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Danger zone */}
      <div className="card" style={{ border: '1px solid var(--red)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, color: 'var(--red-light)' }}>
          <AlertTriangle size={16} />
          <h3 style={{ color: 'var(--red-light)' }}>Danger Zone</h3>
        </div>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 14 }}>
          Clear all locally saved quotes and walk-in data. This cannot be undone. Use only when resetting the app.
        </p>
        {!confirmClear ? (
          <button className="btn btn-danger" onClick={() => setConfirmClear(true)}>
            <Trash2 size={14} /> Clear All Data
          </button>
        ) : (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: 'var(--red-light)' }}>Are you sure? This is permanent.</span>
            <button className="btn btn-danger" onClick={() => { onClearData(); setConfirmClear(false); }}>Yes, clear everything</button>
            <button className="btn btn-secondary" onClick={() => setConfirmClear(false)}>Cancel</button>
          </div>
        )}
      </div>
    </div>
  );
}
